/**
 * Step 4 — Documentos.
 *
 * A lista de requisitos é montada a partir de `layoutFields`: o front não sabe
 * quais documentos existem, ele só renderiza o que o backend habilitou (Lane 2).
 * Cada campo de titular aparece uma vez; cada campo de dependente aparece uma
 * vez por dependente MANTIDO no Step 3 — quem foi desmarcado não pede documento.
 *
 * O upload é imediato (spec §5): escolher o arquivo já sobe e devolve a S3 key,
 * que é o que vai no submit. O `File` local fica só para o preview, em object
 * URLs criadas e revogadas aqui — é este step que possui esse ciclo de vida.
 */

import { Alert, AlertTitle } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { uploadExtensionDocument } from "../api";
import { DocumentPreviewModal } from "../components/DocumentPreviewModal";
import { DocumentRequirementRow } from "../components/DocumentRequirementRow";
import { StepActions } from "../components/StepActions";
import { useExtensionForm } from "../context";
import {
  buildDocumentKey,
  excludeUploadedDocument,
  filterLayoutFields,
  findUploadedDocument,
  sumDocumentSizes,
} from "../helpers";
import { Caption, Header, Stack, TwoColumns } from "../styled";
import { ILayoutField, IUploadedDocument, PersonType } from "../types";

/** Um requisito concreto: um campo do layout aplicado a um beneficiário. */
interface IRequirement {
  key: string;
  field: ILayoutField;
  beneficiaryTaxPayerId: string;
}

/** Preview local do arquivo escolhido, para o modal do "Ver arquivo". */
interface IPreviewSource {
  objectUrl: string;
  mimeType: string;
}

function buildRequirements(
  fields: ILayoutField[],
  personType: PersonType,
  beneficiaryTaxPayerId: string
): IRequirement[] {
  return filterLayoutFields(fields, personType).map((field) => ({
    key: buildDocumentKey(field.fieldId, beneficiaryTaxPayerId),
    field,
    beneficiaryTaxPayerId,
  }));
}

export function Step4Documents() {
  const { organizationId, token, data, state, updateState, goNext, goBack } =
    useExtensionForm();
  const { holder, dependents, layoutFields } = data;

  const [previews, setPreviews] = useState<Record<string, IPreviewSource>>({});
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  /**
   * As object URLs precisam ser revogadas na saída do step. O ref espelha o
   * estado para o cleanup não depender do valor capturado na primeira render.
   */
  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  useEffect(() => {
    return () => {
      Object.values(previewsRef.current).forEach((preview) =>
        URL.revokeObjectURL(preview.objectUrl)
      );
    };
  }, []);

  const holderRequirements = buildRequirements(
    layoutFields,
    "TITULAR",
    holder.taxPayerId
  );

  const retainedDependents = dependents.filter((dependent) =>
    state.retainedDependentTaxPayerIds.includes(dependent.taxPayerId)
  );

  /**
   * O design agrupa em duas colunas — "Titular" e "Dependentes" — e não em um
   * card por pessoa. Como a coluna da direita mistura os dependentes, o nome
   * entra no rótulo da linha ("Certidão - Pedro Silva") para o titular saber de
   * quem é cada documento.
   */
  const dependentRequirements = retainedDependents.flatMap((dependent) =>
    buildRequirements(layoutFields, "DEPENDENTE", dependent.taxPayerId).map(
      (requirement) => ({
        ...requirement,
        field: {
          ...requirement.field,
          label: `${requirement.field.label} - ${dependent.name}`,
        },
      })
    )
  );

  const allRequirements = [...holderRequirements, ...dependentRequirements];

  function getUploaded(requirement: IRequirement) {
    return findUploadedDocument(
      state.uploadedDocuments,
      requirement.field.fieldId,
      requirement.beneficiaryTaxPayerId
    );
  }

  const missingRequired = allRequirements.filter(
    (requirement) => requirement.field.required && !getUploaded(requirement)
  );

  /** "1 de 7 documentos enviados", como o design mostra abaixo da lista. */
  const uploadedRequiredCount = allRequirements.filter((requirement) =>
    getUploaded(requirement)
  ).length;

  async function handleSelectFile(requirement: IRequirement, file: File) {
    const { key, field, beneficiaryTaxPayerId } = requirement;

    setUploadingKey(key);
    setErrors((previous) => {
      const { [key]: _removed, ...rest } = previous;

      return rest;
    });

    /** Um reenvio substitui o anexo anterior, então ele sai da soma do limite. */
    const others = excludeUploadedDocument(
      state.uploadedDocuments,
      field.fieldId,
      beneficiaryTaxPayerId
    );

    try {
      const response = await uploadExtensionDocument(organizationId, token, {
        fieldId: field.fieldId,
        personType: field.personType,
        beneficiaryTaxPayerId,
        file,
        currentTotalSizeBytes: sumDocumentSizes(others),
      });

      if (!response?.s3Key) {
        throw new Error("Resposta do upload sem identificador do arquivo.");
      }

      /**
       * Só a `s3Key` vem do servidor; o resto já se sabe localmente. Fixar aqui
       * garante que o submit não dependa do eco do backend.
       */
      const uploaded: IUploadedDocument = {
        s3Key: response.s3Key,
        fieldId: field.fieldId,
        personType: field.personType,
        beneficiaryTaxPayerId,
        fileName: file.name,
        sizeBytes: file.size,
      };

      updateState({ uploadedDocuments: [...others, uploaded] });

      setPreviews((previous) => {
        if (previous[key]) URL.revokeObjectURL(previous[key].objectUrl);

        return {
          ...previous,
          [key]: {
            objectUrl: URL.createObjectURL(file),
            mimeType: file.type,
          },
        };
      });
    } catch (error) {
      setErrors((previous) => ({
        ...previous,
        [key]:
          error instanceof Error
            ? error.message
            : "Não foi possível enviar o arquivo.",
      }));
    } finally {
      setUploadingKey(null);
    }
  }

  /**
   * A spec §10.2 permite desanexar. Além do estado do formulário, a linha tem
   * recursos locais para soltar: a object URL do preview, que vazaria, e o erro
   * da última tentativa — senão o "Anexar" reaparece já mostrando a falha antiga.
   */
  function handleRemove(requirement: IRequirement) {
    const { key, field, beneficiaryTaxPayerId } = requirement;

    updateState({
      uploadedDocuments: excludeUploadedDocument(
        state.uploadedDocuments,
        field.fieldId,
        beneficiaryTaxPayerId
      ),
    });

    if (previewKey === key) setPreviewKey(null);

    setPreviews((previous) => {
      const preview = previous[key];

      if (!preview) return previous;

      URL.revokeObjectURL(preview.objectUrl);

      const { [key]: _removed, ...rest } = previous;

      return rest;
    });

    setErrors((previous) => {
      const { [key]: _removed, ...rest } = previous;

      return rest;
    });
  }

  const previewRequirement = allRequirements.find(
    (requirement) => requirement.key === previewKey
  );
  const previewUploaded = previewRequirement
    ? getUploaded(previewRequirement)
    : undefined;

  function renderRow(requirement: IRequirement) {
    return (
      <DocumentRequirementRow
        key={requirement.key}
        label={requirement.field.label}
        required={requirement.field.required}
        uploaded={getUploaded(requirement)}
        isUploading={uploadingKey === requirement.key}
        errorMessage={errors[requirement.key]}
        onSelectFile={(file) => handleSelectFile(requirement, file)}
        onPreview={() => setPreviewKey(requirement.key)}
        onRemove={() => handleRemove(requirement)}
      />
    );
  }

  return (
    <Stack $gap="24px">
      <Stack $gap="4px">
        <Header>Documentos</Header>
        <Caption>Formatos aceitos: PDF, JPEG, PNG</Caption>
      </Stack>

      <Alert severity="error">
        <AlertTitle>Envio obrigatório neste momento</AlertTitle>
        Não será possível enviar os documentos após concluir o formulário. Esta é
        sua única oportunidade.
      </Alert>

      <TwoColumns>
        <Stack $gap="8px">
          <Caption>Titular</Caption>
          <div>{holderRequirements.map(renderRow)}</div>
        </Stack>

        {dependentRequirements.length > 0 && (
          <Stack $gap="8px">
            <Caption>Dependentes</Caption>
            <div>{dependentRequirements.map(renderRow)}</div>
          </Stack>
        )}
      </TwoColumns>

      <Caption>
        {uploadedRequiredCount} de {allRequirements.length} documentos enviados
      </Caption>

      <StepActions
        nextLabel="Próximo: Revisar →"
        onNext={goNext}
        onBack={goBack}
        isNextDisabled={missingRequired.length > 0 || !!uploadingKey}
      />

      <DocumentPreviewModal
        open={!!previewKey}
        fileName={previewUploaded?.fileName ?? ""}
        objectUrl={previewKey ? previews[previewKey]?.objectUrl ?? "" : ""}
        mimeType={previewKey ? previews[previewKey]?.mimeType ?? "" : ""}
        onClose={() => setPreviewKey(null)}
      />
    </Stack>
  );
}
